import moment from 'moment'
import React from 'react'
import showdown from 'showdown'
import { capitalize, upperFirst, startCase } from 'lodash'
import { DateTime } from 'luxon'
import { htmlToText } from 'html-to-text'

import Text from 'components/typography/Text'

export const formatDateOfBirth = date => moment(date).format('MM/DD/YYYY')

export const formatAddress = ({ line1, line2, city, state, country, zip }) => [
  [line1, line2].filter(Boolean).join(', '),
  [city, state, zip].filter(Boolean).join(', '),
  country || ''
]

export const formatAppointmentStatus = status => status && startCase(status)

export const formatAge = date => {
  if (!date) return ''

  const ageInYears = moment().diff(moment(date), 'years')

  if (ageInYears < 2) {
    const ageInMonths = moment().diff(moment(date), 'months')
    return `${ageInMonths} month${ageInMonths === 1 ? '' : 's'}`
  }

  return `${ageInYears} year${ageInYears === 1 ? '' : 's'}`
}

export const formatCurrency = string =>
  string &&
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(string)

const reformatDate = (date, format = { fmtMom: null, fmtLux: null }) => {
  const { fmtMom, fmtLux } = format

  if (fmtMom === null || fmtLux === null) return 'DateTime Format Invalid'

  if (typeof date === 'object') return moment(date).format(fmtMom)

  if (typeof date === 'string') {
    let d = DateTime.fromFormat(date, "yyyy-MM-dd HH:mm:ss 'UTC'", {
      zone: 'utc'
    })
    if (d.isValid) return d.toLocal().toFormat(fmtLux)

    d = DateTime.fromISO(date)
    if (d.isValid) return d.toFormat(fmtLux)

    d = moment(date)
    if (d.isValid()) return d.local().utc().format(fmtMom)

    return moment(date).format(fmtMom)
  }

  return `Err: ${typeof date}`
}

export const formatDate = date =>
  date && reformatDate(date, { fmtMom: 'MMM DD, YYYY', fmtLux: 'MMM dd, yyyy' })
// reformatDate(date, "MMM DD, YYYY", "MMM dd, yyyy")

export const formatShortDate = date =>
  date && reformatDate(date, { fmtMom: 'MMM DD', fmtLux: 'MMM dd' })
// moment(date,"YYYY-MM-DD HH:mm:ss UTC").format('MMM DD')

export const formatDateISO = date =>
  date && reformatDate(date, { fmtMom: 'YYYY-MM-DD', fmtLux: 'yyyy-MM-dd' })
// moment(date,"YYYY-MM-DD HH:mm:ss UTC").format('YYYY-MM-DD')

export const formatUTCDateTimeISO = date =>
  date &&
  reformatDate(date, {
    fmtMom: 'YYYY-MM-DDTHH:mm:ss.sssZ',
    fmtLux: "yyyy-MM-dd'T'HH:mm:ss.uZZZ"
  })
// moment.utc(date,"YYYY-MM-DD HH:mm:ss UTC").format('YYYY-MM-DDTHH:mm:ss.sssZ')

export const formatDateAlternate = date =>
  date && reformatDate(date, { fmtMom: 'MM/DD/YYYY', fmtLux: 'MM/dd/yyyy' })
// moment(date,"YYYY-MM-DD HH:mm:ss UTC").format('MM/DD/YYYY')

export function formatYesNo(value) {
  if (value === null) return '-'
  return value ? 'Yes' : 'No'
}

export const formatTime = date =>
  date && reformatDate(date, { fmtMom: 'h:mm a', fmtLux: 'h:mm a' })
// moment(date,"YYYY-MM-DD HH:mm:ss UTC").format('h:mm a')

export const formatShortDateTime = date =>
  date
    ? `${formatShortDate(date)}, ${formatTime(date)}`
    : `${formatShortDate(new Date())}, ${formatTime(new Date())}`

export const formatDateTime = date => {
  if (!date) return '-'
  return `${formatDate(date)}, ${formatTime(date)}`
}

export const parseDateValue = value => {
  if (!value) return null

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }

  if (typeof value === 'string') {
    // Smart note dates can come back from Rails as strings like
    // 2026-04-27 00:00:00 UTC. Chrome is permissive with this format,
    // but Safari can treat it as invalid. Parse the known formats here
    // so the edit flow keeps the saved date instead of falling back to today.
    const parsers = [
      () =>
        DateTime.fromFormat(value, "yyyy-MM-dd HH:mm:ss 'UTC'", {
          zone: 'utc'
        }),
      () => DateTime.fromISO(value),
      () => DateTime.fromRFC2822(value)
    ]

    const parsedDate = parsers
      .map(parser => parser())
      .find(parsedValue => parsedValue.isValid)

    if (parsedDate) return parsedDate.toJSDate()
  }

  const normalizedDate = new Date(value)
  return Number.isNaN(normalizedDate.getTime()) ? null : normalizedDate
}

export const normalizeDateValue = (value, fallbackValue = new Date()) => {
  if (!value) return fallbackValue

  return parseDateValue(value) || fallbackValue
}

export const formatAltDateTime = date =>
  date && `${formatDateAlternate(date)}, ${formatTime(date)}`

export const formatDuration = duration => duration || 0

export const formatName = person => {
  if (!person) return ''

  const { firstName, lastName, fullName } = person || {}

  if (firstName && lastName)
    return `${capitalize(lastName)}, ${capitalize(firstName)}`
  return startCase(fullName)
}

export const formatGender = gender => gender && gender[0].toUpperCase()

export const formatFullGender = gender => gender && upperFirst(gender)

export const formatPatient = ({
  dateOfBirth,
  gender,
  fullName,
  firstName,
  lastName
}) => {
  const formattedName = formatName({ fullName, firstName, lastName })
  const formattedAge = formatAge(dateOfBirth)
  const formattedGender = formatGender(gender)

  let result = `${formattedName}`
  if (dateOfBirth) {
    result += ` ${formattedAge}`
  }
  if (gender) {
    result += dateOfBirth ? `, ${formattedGender}` : ` ${formattedGender}`
  }

  return result
}

// Important : This function is written specifically for
// converting the input date of birth coming in differnt ISO
// formats to a specific format i.e. MM/dd/yyyy
// Also note that there is no conversion to the local time and
// is printed as in received from the backend
export const formatDisplayDateOfBirth = inputDateOfBirth => {
  // check if valid ISO date
  if (!DateTime.fromISO(inputDateOfBirth).isValid) return inputDateOfBirth

  // check if time is present in date,
  // remove time and extract year,month and day
  const dob = inputDateOfBirth.includes('T')
    ? inputDateOfBirth.split('T')[0]
    : inputDateOfBirth
  const [year, month, day] = dob.split('-')

  return `${month}/${day}/${year}`
}

export const formatProviderName = ({ lastName }) =>
  lastName && `Dr. ${lastName}`

export const formatRole = role =>
  role
    .split('_')
    .map(roleSegment => upperFirst(roleSegment))
    .join(' ')

export const formatHeight = height => {
  const feet = Math.floor(height / 12)
  const inches = height % 12
  return height ? `${feet}'${inches}"` : null
}

export const formatWeight = weight => (weight ? `${weight} lbs` : null)

export const formatDateCountDown = date => {
  const total = Date.parse(date) - Date.parse(new Date())
  const days = Math.floor(total / (1000 * 60 * 60 * 24))
  const absoluteDays = Math.abs(days)
  const dayOrDays = absoluteDays === 1 ? 'day' : 'days'
  const daysInPastOrFuture = Math.sign(days) === -1 ? 'ago' : 'to go'
  return `${absoluteDays} ${dayOrDays} ${daysInPastOrFuture}`
}

export const formatAppointmentOperativeNotes = (patient, operativeNotes) => {
  const { firstName, lastName } = patient || {}
  const { createdAt } = operativeNotes

  return `OpRep ${lastName}, ${firstName[0]} ${reformatDate(createdAt, {
    fmtMom: 'DD MMM YYYY',
    fmtLux: 'dd MMM yyyy'
  })}`
}

export const formatOnCallOperativeNotes = (patientName, operativeNotes) => {
  const { documentGeneratedAt } = operativeNotes

  return `OpRep ${patientName} ${moment(documentGeneratedAt).format(
    'DD MMM YYYY'
  )}`
}

export const formatURL = (url, shrinkLength = 15) =>
  `${url.slice(0, shrinkLength)} ... ${url.slice(-shrinkLength)}`

export const saveDateISOFormat = date => new Date(date).toJSON()

export const formatIntegerToOrdinal = number => {
  const scaledNumber =
    number % 100 > 10 && number % 100 < 20
      ? number % 100
      : (number % 100) - ((number % 100) - (20 + (number % 10)))

  switch (scaledNumber) {
    case 21:
      return `${number}st`
    case 22:
      return `${number}nd`
    case 23:
      return `${number}rd`
    default:
      return `${number}th`
  }
}

// Formats an array of strings, eg
// Zero items: ""
// One item: "Megan"
// Two items: "Megan and Molly"
// Three items: "Megan, Molly and Sean"
// Four items: "Megan, Molly, Sean and Jared"
export const formatList = array => {
  if (array.length < 1) return ''
  if (array.length === 1) return array[0]

  const firsts = array.slice(0, array.length - 1)
  const last = array[array.length - 1]
  return `${firsts.join(', ')} and ${last}`
}

export const formatPatientOptionLabel = (showDateOfBirth, patient) => (
  <div>
    {formatName(patient)}
    {showDateOfBirth && (
      <Text weight="light" color="dark" size="xSmall">
        {formatDate(patient.dateOfBirth)}
      </Text>
    )}
  </div>
)

export const formatPatientValueLabel = (showDateOfBirth, patient) => {
  if (showDateOfBirth) {
    return `${formatName(patient)}, ${formatDateAlternate(patient.dateOfBirth)}`
  }

  return formatName(patient)
}

export const formatPhoneNumber = phoneNumberString => {
  if (phoneNumberString?.length !== 10) return phoneNumberString

  const cleanString = `${phoneNumberString}`.replace(/\D/g, '')
  const match = cleanString.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/)

  if (match) {
    const intlCode = match[1] ? '+1 ' : ''
    return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('')
  }
  return null
}

export const formatStringWithArticle = string =>
  string && 'aeiouAEIOU'.indexOf(string.charAt(0)) !== -1
    ? `an ${string}`
    : `a ${string}`

export const formatTimeFromMilliseconds = ms => {
  const seconds = (ms / 1000).toFixed(1)
  const minutes = (ms / (1000 * 60)).toFixed(1)
  const hours = (ms / (1000 * 60 * 60)).toFixed(1)
  const days = (ms / (1000 * 60 * 60 * 24)).toFixed(0)
  if (ms === 0 || Number.isNaN(ms) || !ms) return '-'

  if (seconds < 60) {
    return `${seconds} ${seconds > 1 ? 'secs' : 'sec'}`
  }
  if (minutes < 60) {
    return `${minutes} ${minutes > 1 ? 'mins' : 'min'}`
  }
  if (hours < 24) {
    return `${hours} ${hours > 1 ? 'hrs' : 'hr'}`
  }
  return `${days} ${days > 1 ? 'days' : 'day'}`
}

export const formatTimeFromSeconds = seconds =>
  formatTimeFromMilliseconds(seconds * 1000)

export const timeFromMillisecondsToSeconds = ms => Math.trunc(ms / 1000)

export const timeFromMillisecondsToMinutes = ms => Math.trunc(ms / 1000) / 60

export const parseValidJSON = jsonString =>
  JSON.parse(jsonString?.replace(/,\s*([\\}\]])/g, '$1'))

export const formatAINotesContent = content => {
  if (!content) return ''

  return Array.isArray(content)
    ? content.reduce((acc, x) => `${acc.replace(/^,/, '')}- ${x}\n`, '')
    : content?.replace(/^- \d+\.\s+/gm, '- ').replace(/- *• */g, '- ')
}

// Helper function to convert markdown to HTML
function applyMarkdownFormatting(text) {
  const converter = new showdown.Converter()

  return converter.makeHtml(text)
}

function isValidHTML(htmlString) {
  const doc = new DOMParser().parseFromString(htmlString, 'text/html')

  if (doc.querySelector('parsererror')) {
    return false
  }

  return [...doc.body.childNodes].some(node => node.nodeType === 1) // Node.ELEMENT_NODE (1)
}

export function formatAIContentToHTML(content) {
  // Return if content is already a valid HTML
  if (isValidHTML(content)) return content

  // Split content into lines, trim whitespace, and filter out empty lines
  const lines = formatAINotesContent(content)
    .trim()
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0)

  const sections = []
  let currentSection = { title: '', items: [] }
  let isNumbered = false
  const plainText = []

  lines.forEach(line => {
    isNumbered = /^\d+\./.test(line)
    if (!line.startsWith('-') && !isNumbered) {
      // Save the previous section if it has items
      sections.push(currentSection)
      currentSection = {
        title: applyMarkdownFormatting(line),
        items: [],
        isNumbered
      }
    } else {
      currentSection.items.push(
        applyMarkdownFormatting(
          isNumbered
            ? line.replace(/^\s*\d+\.\s*/gm, '')
            : line.replace(/^- */gm, '')
        )
      )
      currentSection.isNumbered = isNumbered
    }
  })

  // Push the last section
  if (currentSection.items.length > 0) {
    sections.push(currentSection)
  } else if (currentSection.title) {
    plainText.push(`<p>${currentSection.title}</p>`)
  }

  const remainingSectionHTML = plainText
    .map(line =>
      line
        .replace(/<(\w+)[^>]*>\s*<\/\1>/gi, '')
        .replace(/^(<br\s*\/?>\s*)+/gi, '')
    )
    .join('\n')
    .trim()

  // Convert to formatted HTML
  const sectionsHTML = sections
    .map(({ title, items, isNumbered: isNumberedList }) => {
      const listTag = isNumberedList ? 'ol' : 'ul'
      const titleHTML = title ? `<p>${title}</p>` : ''
      const listHTML = `<${listTag}>${items
        .map(item => `<li>${item.replace(/<\/?p>/g, '')}</li>`)
        .join('')}</${listTag}><br>`
      return titleHTML + listHTML
    })
    .join('')
    // Remove empty tags from the above generated HTML
    .replace(/<(\w+)[^>]*>\s*<\/\1>/gi, '')
    .replace(/^(<br\s*\/?>\s*)+/gi, '')

  return sectionsHTML + remainingSectionHTML
}

export const formatSnakeCaseToCamelCase = string =>
  string.replace(/_([a-z])/g, (_, lowerCaseLetter) =>
    lowerCaseLetter.toUpperCase()
  )

export const cleanHtmlContent = content =>
  content
    ?.replace(
      /(\s)?(<(\/?(?:b|strong|u|em))[^>]*>)(\s)?/g,
      (_match, before, tag, _, after) => {
        const nbspBefore = before ? '&nbsp;' : ''
        const nbspAfter = after ? '&nbsp;' : ''
        return `${nbspBefore}${tag}${nbspAfter}`
      }
    )
    .replaceAll(/<br-o>/g, '<br>') || content

export const timeFromMillisecondsHourMinSec = ms => {
  const hours = Math.floor(ms / 3600)
  const mins = Math.floor((ms % 3600) / 60)
  const secs = ms % 60
  return (
    `${hours.toString().padStart(2, '0')}:${mins
      .toString()
      .padStart(2, '0')}:${secs.toString().padStart(2, '0')}` || '00:00:00'
  )
}

export const parseEscapedTextToHTML = escapedText => {
  // Decode Unicode escape sequences to proper characters
  const decodedText = escapedText
    .replace(/\\u003c/g, '<')
    .replace(/\\u003e/g, '>')
    .replace(/\\u0026/g, '&')
    .replace(/\\u0022/g, '"') // Double quote
    .replace(/\\u0027/g, "'") // Single quote

  // Sanitize to prevent XSS by escaping script tags and unsafe content
  const tempDiv = document.createElement('div')
  tempDiv.textContent = decodedText // Safely set decoded content as text
  const safeText = tempDiv.innerHTML

  // Use DOMParser to parse the now-safe HTML
  const parser = new DOMParser()
  const parsedHTML = parser.parseFromString(safeText, 'text/html')

  return parsedHTML.body.innerHTML
}

export const getDateOnly = dateStr =>
  new Date(dateStr).toISOString().split('T')[0]

export const getDateMonth = date => moment(date).format('MM/DD')

export const getWeekDayName = date => moment(date).format('dddd')

export const formatTextForPlainCopy = content => {
  if (!content) return ''

  const sanitizedContent = content.replace(
    /<p>\s*(<br\s*\/?>\s*)+<\/p>/gi,
    '<br>'
  )

  const options = {
    leadingLineBreaks: 1,
    trailingLineBreaks: 1
  }

  const text = htmlToText(sanitizedContent, {
    wordwrap: false,
    preserveNewlines: true,
    selectors: [
      {
        selector: 'br',
        format: 'lineBreak',
        options: { newline: '\n' }
      },
      {
        selector: 'p',
        options
      },
      {
        selector: 'ol',
        options
      },
      {
        selector: 'ul',
        options
      }
    ]
  })

  return text.trim()
}

export const formatArrayToString = items => {
  if (items?.length === 0) return ''
  if (items?.length === 1) return items[0]
  if (items?.length === 2) return items.join(' and ')
  return `${items?.slice(0, -1).join(', ')}, and ${
    items && items[items.length - 1]
  }`
}

export const getComparableValues = (source = {}, fields = []) =>
  fields.reduce((acc, field) => {
    acc[field] = source[field] ?? ''
    return acc
  }, {})

export const getAge = date => {
  if (!date) return ''

  const ageInYears = moment().diff(moment(date), 'years')

  return ageInYears
}

export default {
  cleanHtmlContent,
  formatAddress,
  formatAge,
  formatAINotesContent,
  formatAltDateTime,
  formatAppointmentOperativeNotes,
  formatArrayToString,
  formatDate,
  formatDateAlternate,
  formatDateCountDown,
  formatDateISO,
  formatDateTime,
  formatDuration,
  formatFullGender,
  formatGender,
  formatHeight,
  formatIntegerToOrdinal,
  formatName,
  formatOnCallOperativeNotes,
  formatPatientOptionLabel,
  formatPhoneNumber,
  formatProviderName,
  formatRole,
  formatShortDate,
  formatSnakeCaseToCamelCase,
  formatTextForPlainCopy,
  formatTime,
  formatTimeFromMilliseconds,
  formatTimeFromSeconds,
  formatURL,
  formatUTCDateTimeISO,
  formatWeight,
  getDateMonth,
  getDateOnly,
  getWeekDayName,
  parseDateValue,
  parseEscapedTextToHTML,
  saveDateISOFormat,
  timeFromMillisecondsToSeconds,
  getAge
}
